import { ReferenceDateModeToggle } from "@/features/templates/components/referenceDate/ReferenceDateModeToggle";
import { effectiveReferenceDateMode, type ReferenceDateModeT } from "@/features/templates/utils/referenceDateMode";
import { useMemo, useState } from "react";
import type { TemplateSettingsSchemaT } from "@patient-watch/types";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { toast } from "@patient-watch/ui/sonner";
import { useAuth } from "@/utils/auth";
import { services as s } from "@/services";
import { createGetDiaryKey, diaryByIdQueryOptions } from "@/features/diaries/api";
import { useSelectStaff } from "@/features/profile/api/selectStaff";
import { useSelectForms } from "@/features/forms/api/selectForms";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useInjectionMethodsQuery } from "@/features/injection/api/selectInjectionMethods";
import { deriveUpdateMode } from "@/features/templates/hooks/deriveUpdateMode";
import DiarySummaryForm from "@/features/templates/components/DiarySummaryForm";
import useUsersOrganisationsRolesQuery from "@/features/auth/api/selectUsersOrganisationsRoles";
import { uniqueOrganisationsFromRoles } from "@/features/auth/utils/uniqueOrganisationsFromRoles";
import { applyDiaryUpdateFromTemplate } from "@/features/patients/update/applyDiaryUpdateFromTemplate";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useToastCopy } from "@/features/i18n/toastCopy";
import {
    updateDiaryFlowSearchSchema,
    type UpdateDiaryFlowSearch,
} from "@/features/patients/update/updateDiaryFlowSearch";
import { useSelectSurgeryOptions } from "@/features/events/features/surgeries/api/selectSurgeryOptions";

function normalizeSearch(search: UpdateDiaryFlowSearch): TemplateSettingsSchemaT {
    return {
        ...search,
        required_events: search.required_events ?? [],
        trackers: search.trackers ?? [],
    } as TemplateSettingsSchemaT;
}

function getPatientsReturnSearch(search: UpdateDiaryFlowSearch) {
    return {
        columns: search.columns,
        style: search.style,
        tab: search.tab,
        diaries: search.diaries,
        organisations: search.organisations,
        diary_access: search.diary_access,
        diary_types: search.diary_types,
        labels: search.labels,
        pagination: search.pagination,
        sorting: search.sorting,
        refetcher: search.refetcher,
    };
}

export const Route = createFileRoute("/_app/patients/$diaryId/update/from-template")({
    component: UpdateDiaryFromTemplatePage,
    validateSearch: updateDiaryFlowSearchSchema,
    loader: async ({ context, params }) => {
        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        return {
            defaultContactId: diary?.shared_attributes?.contact_id ?? null,
            postUpdateAction: diary?.allowed_actions?.find((a) => a.action_key === "POST_UPDATE_MESSAGE") ?? null,
        };
    },
});

function UpdateDiaryFromTemplatePage() {
    const copy = usePatientsCopy();
    const toastCopy = useToastCopy();
    const { diaryId } = Route.useParams();
    const search = Route.useSearch();
    const { defaultContactId, postUpdateAction } = Route.useLoaderData();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const auth = useAuth();
    const { data: userOrgRoles = [] } = useUsersOrganisationsRolesQuery(auth.id);
    const { data: staffData = [] } = useSelectStaff();
    const { data: formsData = [] } = useSelectForms();
    const { data: schedulesData = [] } = useSchedulesQuery();
    const { data: injectionMethods = [] } = useInjectionMethodsQuery();
    const { data: surgeryOptions = [] } = useSelectSurgeryOptions();

    const [isSubmitting, setIsSubmitting] = useState(false);
    // Existing patients have always started today, so that is the default unless the template says otherwise.
    const [dateMode, setDateMode] = useState<ReferenceDateModeT>(() =>
        effectiveReferenceDateMode(search.reference_date_mode, "diary_start")
    );
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);

    const organisations = useMemo(() => uniqueOrganisationsFromRoles(userOrgRoles), [userOrgRoles]);

    const mergedSearch = useMemo((): TemplateSettingsSchemaT => {
        const normalized = normalizeSearch(search);
        return {
            ...normalized,
            contact_id: search.contact_id ?? defaultContactId ?? undefined,
            quickstart_key: normalized.quickstart_key || "PAIN",
        } as TemplateSettingsSchemaT;
    }, [search, defaultContactId]);

    const isAdmin = Boolean(auth?.roles?.isAdmin || auth?.isSuperUser);

    const finishWithoutNotification = () => {
        setIsNotifyDialogOpen(false);
        void navigate({
            to: "/patients/$diaryId",
            params: { diaryId },
            search: (prev) => ({ ...prev, ...getPatientsReturnSearch(search) }),
        });
    };

    const notifyPatient = () => {
        if (!postUpdateAction) {
            finishWithoutNotification();
            return;
        }
        setIsNotifyDialogOpen(false);
        void navigate({
            to: "/patients/$diaryId/action/$actionId",
            params: { diaryId, actionId: s.uuidv4() },
            search: { action: postUpdateAction },
        });
    };

    return (
        <>
            <div className="mx-auto max-w-2xl">
                <DiarySummaryForm
                    search={mergedSearch}
                    organisations={organisations}
                    staff={staffData as never}
                    forms={formsData as never}
                    schedules={schedulesData as never}
                    injectionMethods={injectionMethods}
                    surgeryOptions={surgeryOptions}
                    isAdmin={isAdmin}
                    updateFromTemplateDiaryId={diaryId}
                    hideInviteStepper
                    compactVerticalSpacing
                    headerSlot={
                        <div className="space-y-1">
                            <h1 className="font-semibold text-2xl">Confirm template</h1>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                Review what will be added. Details come from your template; submit to save in one step.
                            </p>
                        </div>
                    }
                    footerSlot={({ getValues }) => (
                        <div className="space-y-4">
                            <ReferenceDateModeToggle value={dateMode} onChange={setDateMode} disabled={isSubmitting} />
                            <Button
                                size="lg"
                                className="w-full"
                                type="button"
                                disabled={isSubmitting}
                                onClick={() =>
                                    void (async () => {
                                        if (!auth?.id) {
                                            toast.error(toastCopy.mustBeSignedIn);
                                            return;
                                        }
                                        const data = getValues();
                                        const contact_id = data.contact_id ?? defaultContactId;
                                        if (!contact_id) {
                                            toast.error(toastCopy.contactPersonRequired);
                                            return;
                                        }
                                        const mode = deriveUpdateMode(data);

                                        setIsSubmitting(true);
                                        try {
                                            await applyDiaryUpdateFromTemplate({
                                                diaryId,
                                                settings: {
                                                    ...search,
                                                    ...data,
                                                    contact_id,
                                                    type: mode,
                                                    reference_date_mode: dateMode,
                                                },
                                                authUserId: auth.id,
                                                pausedTrackerIds: [],
                                                activeTrackers: [],
                                            });
                                            toast.success(copy.diaryUpdatedFromTemplate);
                                            await queryClient.invalidateQueries({
                                                queryKey: createGetDiaryKey(diaryId),
                                            });
                                            if (postUpdateAction) {
                                                setIsNotifyDialogOpen(true);
                                            } else {
                                                finishWithoutNotification();
                                            }
                                        } catch (e) {
                                            toast.error(e instanceof Error ? e.message : copy.couldNotApplyTemplate);
                                        } finally {
                                            setIsSubmitting(false);
                                        }
                                    })()
                                }
                            >
                                {isSubmitting ? copy.saving : copy.addToDiaryTitle}
                            </Button>
                        </div>
                    )}
                />
            </div>
            <Dialog
                open={isNotifyDialogOpen}
                onOpenChange={(open) => {
                    if (open) {
                        setIsNotifyDialogOpen(true);
                    } else {
                        finishWithoutNotification();
                    }
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{copy.notifyPatientTitle}</DialogTitle>
                        <DialogDescription>{copy.notifyPatientUpdatedDescription}</DialogDescription>
                    </DialogHeader>
                    <div className="gap-3 grid grid-cols-2 pt-2">
                        <fc.CancelButton
                            variant="outline"
                            size="default"
                            label={copy.noJustFinish}
                            onClick={finishWithoutNotification}
                        />
                        <fc.ConfirmButton size="default" label={copy.yesNotifyPatient} onClick={notifyPatient} />
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
